import type { RouterInputs } from '@client/trpc/client';
import type { FieldConfigurationEntity, FileParentEntity, Role } from '@shared/constants';
import { hasPermission } from '@shared/permissions/roles';

/** Keep prefetched counts fresh long enough to avoid duplicate route preloads. */
export const USER_OWNED_ITEMS_COUNTS_PREFETCH_OPTIONS = {
    staleTime: 30_000,
} as const;

/** Bridge the prefetch-to-mount gap without hiding mutations on a quick return home. */
export const USER_OWNED_ITEMS_COUNTS_QUERY_OPTIONS = {
    staleTime: 3_000,
} as const;

/** Keep the prefetched My items extras fresh like the counts; silent and out of Sentry for flag-off `NOT_FOUND`. */
export const MY_ITEMS_EXTRAS_PREFETCH_OPTIONS = {
    staleTime: 30_000,
    meta: { errorSurface: 'silent', captureToSentry: false },
} as const;

/** Bridge the extras prefetch-to-mount gap without hiding changes on a quick return home. */
export const MY_ITEMS_EXTRAS_QUERY_OPTIONS = {
    staleTime: 3_000,
} as const;

/** Bridge the beforeLoad prefetch to mount gap of a document library's file list and filter data. */
export const DOCUMENT_LIBRARY_QUERY_OPTIONS = {
    staleTime: 3_000,
} as const;

export const ORG_SETTINGS_QUERY_OPTIONS = {
    staleTime: 30_000,
} as const;

/**
 * Keep detail-route `beforeLoad` prefetches fresh long enough to avoid duplicate
 * route preloads. Speculative prefetch failures are silent and skip Sentry — the
 * mount-time `useQuery` retries and owns error surfacing with its own meta.
 */
export const DETAIL_RECORD_PREFETCH_OPTIONS = {
    staleTime: 30_000,
    meta: { errorSurface: 'silent', captureToSentry: false },
} as const;

/**
 * Document library route navigation prefetch: the mount observers' freshness window, so a list
 * they would refetch on mount is refreshed from `beforeLoad` before the page renders, and they
 * join that request. Silent and kept out of Sentry like the other detail-route prefetches.
 */
export const DOCUMENT_LIBRARY_PREFETCH_OPTIONS = {
    staleTime: DOCUMENT_LIBRARY_QUERY_OPTIONS.staleTime,
    meta: DETAIL_RECORD_PREFETCH_OPTIONS.meta,
} as const;

/** Keep the mount observer aligned with the route-prefetch freshness window. */
export const DETAIL_RECORD_QUERY_OPTIONS = {
    staleTime: DETAIL_RECORD_PREFETCH_OPTIONS.staleTime,
} as const;

/**
 * Claim details mount observer: bridge the `beforeLoad` prefetch → mount gap without
 * hiding list-level changes (e.g. bulk reassignment, which does not invalidate
 * `claim.getClaim`) on a quick return.
 */
export const CLAIM_RECORD_QUERY_OPTIONS = {
    staleTime: 5_000,
} as const;

/**
 * Claim details route navigation prefetch: the mount observer's freshness window, so a
 * record the observer would refetch is refreshed from `beforeLoad` instead of on mount.
 * Silent and kept out of Sentry like the other detail-route prefetches.
 */
export const CLAIM_RECORD_PREFETCH_OPTIONS = {
    staleTime: CLAIM_RECORD_QUERY_OPTIONS.staleTime,
    meta: DETAIL_RECORD_PREFETCH_OPTIONS.meta,
} as const;

/**
 * Obligation details mount observer: bridge the `beforeLoad` prefetch → mount gap. Kept short
 * because the obligations list's bulk actions (`ObligationReassignmentDrawer`'s
 * `bulkUpdateObligationOwner` and `ObligationStatusUpdateModal`'s `bulkUpdateObligationsStatus`)
 * invalidate only `obligation.listObligations` and `listObligationsGroupedByHandlingParties`, not
 * `obligation.getObligation`, so a 30 s window would show a stale owner or status on a quick return.
 */
export const OBLIGATION_RECORD_QUERY_OPTIONS = {
    staleTime: 5_000,
} as const;

/**
 * Obligation details route navigation prefetch: the mount observer's freshness window, so the
 * observer joins the `beforeLoad` request instead of refetching on mount. Silent and kept out of
 * Sentry like the other detail-route prefetches.
 */
export const OBLIGATION_RECORD_PREFETCH_OPTIONS = {
    staleTime: OBLIGATION_RECORD_QUERY_OPTIONS.staleTime,
    meta: DETAIL_RECORD_PREFETCH_OPTIONS.meta,
} as const;

/**
 * Detail-page lookups that eager cards render from (custom field configurations, the ASIC
 * product tree, the incident Obligations card's first page and linked obligation IDs). The
 * window only bridges the `beforeLoad` prefetch → mount gap (at most ~0.6 s on navigations
 * and ~1.1 s on pageloads in production traces), so the mount observer reuses the prefetch
 * instead of sending a second batch after the record renders. Kept short because custom-field
 * archive, restore and reorder (`FieldConfigurationList`) do not invalidate the renderer's
 * key, so a longer window would hide them on a quick return. The obligations card refetches
 * or is invalidated explicitly after linking and unlinking.
 */
export const DETAIL_LOOKUP_QUERY_OPTIONS = {
    staleTime: 5_000,
} as const;

/**
 * Detail route navigation prefetch of those lookups: the mount observer's freshness window,
 * silent and kept out of Sentry like the other detail-route prefetches.
 */
export const DETAIL_LOOKUP_PREFETCH_OPTIONS = {
    staleTime: DETAIL_LOOKUP_QUERY_OPTIONS.staleTime,
    meta: DETAIL_RECORD_PREFETCH_OPTIONS.meta,
} as const;

/**
 * Org-level config lookups (selector option lists). Stable enough to survive the
 * full prefetch → data → tab-mount gap even on slow loads.
 */
export const CONFIG_LOOKUP_QUERY_OPTIONS = {
    staleTime: 30_000,
} as const;

/**
 * List-route `beforeLoad` prefetch window (CUR-1036). The claims, complaints and incidents
 * lists use it for hover-intent preloads and for navigations that keep the list matched
 * (`'stay'`); their entering navigations use `LIST_ROUTE_NAVIGATION_PREFETCH_OPTIONS`.
 * The training register still uses it for every cause (its records list, and its periods
 * list through `TRAINING_PERIODS_LIST_PREFETCH_OPTIONS`), so a register return 3–15 s
 * after the last fetch still leaves the records refetch to the mount observer.
 * Shorter freshness than detail records: list rows are change-sensitive, so the
 * window only needs to bridge prefetch → mount. Silent + no Sentry for the
 * speculative fetch; the mount-time `useQuery` owns retries and error surfacing.
 */
export const LIST_ROUTE_PREFETCH_OPTIONS = {
    staleTime: 15_000,
    meta: { errorSurface: 'silent', captureToSentry: false },
} as const;

/** Bridge the prefetch-to-mount gap without hiding changes on a quick return. */
export const LIST_ROUTE_QUERY_OPTIONS = {
    staleTime: 3_000,
} as const;

/**
 * List-route navigation prefetch: the mount observer's freshness window, so a list the
 * observer would refetch on mount is refreshed from `beforeLoad` before the page renders,
 * and the observer joins that request. Silent and kept out of Sentry like the hover window.
 */
export const LIST_ROUTE_NAVIGATION_PREFETCH_OPTIONS = {
    staleTime: LIST_ROUTE_QUERY_OPTIONS.staleTime,
    meta: LIST_ROUTE_PREFETCH_OPTIONS.meta,
} as const;

/** One cache key for the training register's prefetch and the training user page; here because the register's `beforeLoad` is in the entry graph (as `buildPublicFieldConfigurationsInput`). */
export const TRAINING_PERIODS_LIST_INPUT = {
    orderBy: { dateTo: 'desc' },
} as const satisfies RouterInputs['trainingPeriod']['listTrainingPeriods'];

/**
 * Nothing on the register observes the periods list it warms, so React Query's default 5-min gcTime would drop it
 * during a longer register dwell and the training user page would request its records only after the periods again.
 * Both the register's prefetch and the user page's observer carry it, because a prefetch that finds the entry fresh
 * does not update its options. A stale entry still picks the default period and revalidates in the background. Org
 * switch clears the cache, so this cannot keep another org's periods.
 */
const TRAINING_PERIODS_LIST_GC_TIME = 60 * 60_000;

/** The training register's `beforeLoad` warm-up of the training user page's periods list. */
export const TRAINING_PERIODS_LIST_PREFETCH_OPTIONS = {
    ...LIST_ROUTE_PREFETCH_OPTIONS,
    gcTime: TRAINING_PERIODS_LIST_GC_TIME,
} as const;

/** The training user page's periods list: org configuration, fresh for longer than the register's prefetch. */
export const TRAINING_PERIODS_LIST_QUERY_OPTIONS = {
    ...CONFIG_LOOKUP_QUERY_OPTIONS,
    gcTime: TRAINING_PERIODS_LIST_GC_TIME,
} as const;

/**
 * Sections the public complaint form renders custom fields for, in render order.
 * The complaint route prefetches each one so the form's renderers hit a warm cache.
 */
export const PUBLIC_COMPLAINT_FIELD_SECTIONS = ['complainant', 'nature_of_complaint'] as const;

/**
 * Builds the `listPublicFieldConfigurations` input. Shared by the renderer and route
 * prefetches so both produce the same React Query cache key. It lives here rather
 * than beside the renderer because the complaint route's `beforeLoad` imports it
 * eagerly: a module shared by the entry graph and a lazy chunk becomes its own
 * preloaded chunk, and this module is already in the entry closure.
 */
export function buildPublicFieldConfigurationsInput({
    orgId,
    entity,
    entityId,
    section,
}: {
    orgId: bigint;
    entity: 'PRODUCT_TYPE' | 'COMPLAINT';
    entityId?: bigint;
    section?: string;
}): RouterInputs['fieldConfiguration']['listPublicFieldConfigurations'] {
    return {
        where: {
            orgId,
            entity,
            entityId,
            ...(section
                ? {
                      section: {
                          in: [section],
                      },
                  }
                : {}),
        },
    };
}

/**
 * Builds the `listFieldConfigurations` input of `FieldConfigurationViewRenderer`. Shared with
 * detail-route prefetches so both produce the same React Query cache key; here rather than
 * beside the renderer for the entry-graph reason given on `buildPublicFieldConfigurationsInput`.
 */
export function fieldConfigurationViewInput(
    entity: FieldConfigurationEntity,
    entityId?: bigint,
): RouterInputs['fieldConfiguration']['listFieldConfigurations'] {
    return { where: { entity, entityId } };
}

/**
 * The complaints list's `listFieldConfigurations` input (its configurable columns). Shared with
 * the list route's `beforeLoad` prefetch so both produce the same React Query cache key.
 */
export const COMPLAINT_LIST_FIELD_CONFIGURATIONS_INPUT = {
    where: { entity: 'COMPLAINT', isArchived: false },
} as const satisfies RouterInputs['fieldConfiguration']['listFieldConfigurations'];

/**
 * The claims filter bar's `listInsurersForSelect` input. Shared with the list route's `beforeLoad`
 * prefetch so both produce the same React Query cache key; here rather than beside the filter hook
 * because the route's `beforeLoad` is in the entry graph.
 */
export const CLAIM_FILTER_INSURERS_INPUT = {
    orderBy: { name: 'asc' },
} as const satisfies RouterInputs['insurer']['listInsurersForSelect'];

/**
 * The complaints filter bar's `listHandlingParties` input: every party for an org admin, otherwise
 * the user's own. Shared with the list route's `beforeLoad` prefetch for the same cache key.
 */
export function complaintFilterHandlingPartiesInput(user: {
    id: bigint;
    roles: Role[];
}): RouterInputs['handlingParty']['listHandlingParties'] {
    return {
        where: {
            ...(hasPermission(user.roles, 'org:admin') ? {} : { UsersInHandlingParty: { some: { userId: user.id } } }),
        },
    };
}

/** `VAsicProductSelect`'s full-tree input (config and defaults), shared with detail-route prefetches. */
export const ASIC_PRODUCTS_FULL_TREE_INPUT = {
    includeHidden: true,
} as const satisfies RouterInputs['asicProduct']['listAsicProducts'];

/** The files a document library lists: its parent record, optionally with the files of related records. */
type DocumentLibraryScope = {
    entity: FileParentEntity;
    entityId: bigint;
    shouldIncludeRelatedEntities?: boolean;
};

/**
 * `DocumentLibrary`'s `listFiles` input as it first mounts (newest first, no deleted files, no
 * filters). Shared with Library route prefetches so both produce the same React Query cache key;
 * here rather than beside the component for the entry-graph reason given on
 * `buildPublicFieldConfigurationsInput`. `includeDeleted: false` must stay explicit: the query key
 * hash drops `undefined` but keeps `false`.
 */
export function documentLibraryListInput({
    entity,
    entityId,
    shouldIncludeRelatedEntities,
}: DocumentLibraryScope): RouterInputs['file']['listFiles'] {
    return {
        orderBy: [{ createdAt: 'desc' }],
        includeDeleted: false,
        where: { entity, entityId, shouldIncludeRelatedEntities },
    };
}

/** `FileFilters`' `getDataForFileFilters` input, shared with Library route prefetches for the same cache key. */
export function documentLibraryFilterDataInput({
    entity,
    entityId,
    shouldIncludeRelatedEntities,
}: DocumentLibraryScope): RouterInputs['file']['getDataForFileFilters'] {
    return { entity, entityId, shouldIncludeRelatedEntities };
}
